import React, { useMemo } from 'react';
import {
  Card,
  Collapse,
  Descriptions,
  Empty,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import {
  SYNTHETIC_BOND_FAMILY_METADATA_FIELDS,
  SyntheticBondBatchPreview,
  SyntheticBondCandidate,
  SyntheticBondGenerationDraft,
  SyntheticBondReferenceCurvesResponse,
  SyntheticBondFinding,
  syntheticBondCandidateId,
  syntheticBondSourceNodeMonths,
  syntheticBondTenorLabel,
} from 'state';

type Props = {
  draft: SyntheticBondGenerationDraft;
  preview: SyntheticBondBatchPreview;
  referenceCurves?: SyntheticBondReferenceCurvesResponse;
  onSelectCandidate: (candidate: SyntheticBondCandidate) => void;
};

type PreviewSourceNode = {
  tenor_months?: number;
  tenor_years?: number;
  tenor_label?: string;
  source_asset_id: number;
  model: string;
  name: string;
  ticker?: string | null;
  ric?: string | null;
};

const findingsFor = (candidate: SyntheticBondCandidate) => [
  ...(candidate.findings || []),
  ...(candidate.warnings || []),
  ...(candidate.hard_blockers || []).map(finding => ({
    ...finding,
    severity: finding.severity || 'ERROR',
  })),
  ...(candidate.blockers || []).map(finding => ({
    ...finding,
    severity: finding.severity || 'ERROR',
  })),
];

const candidateIdentifiers = (candidate: SyntheticBondCandidate) => ({
  name: candidate.name || candidate.identifiers?.name,
  ticker: candidate.ticker || candidate.identifiers?.ticker,
  ric: candidate.ric || candidate.identifiers?.ric,
  isin: candidate.isin || candidate.identifiers?.isin,
});

const findingText = (finding: SyntheticBondFinding) =>
  finding.message || finding.detail || finding.code || 'Finding';

const dateOnly = (value: unknown) =>
  typeof value === 'string' && value ? value.slice(0, 10) : '—';

const timestamp = (candidate: SyntheticBondCandidate) =>
  candidate.fixing_source_timestamp ||
  candidate.fixing_timestamp ||
  (candidate.fixing && typeof candidate.fixing === 'object'
    ? String(
        (candidate.fixing as Record<string, unknown>).timestamp ||
          (candidate.fixing as Record<string, unknown>).source_timestamp ||
          '',
      )
    : '');

const candidateTenor = (candidate: SyntheticBondCandidate) =>
  Number(candidate.tenor_years || candidate.initial_tenor_years || 0);

const previewCandidates = (preview: SyntheticBondBatchPreview) => {
  const direct = preview.items || preview.candidates;
  if (direct?.length) return direct;
  return (preview.tenor_summaries || []).flatMap(summary => {
    const items = summary.items || summary.candidates;
    return Array.isArray(items) ? (items as SyntheticBondCandidate[]) : [];
  });
};

export const syntheticBondPreviewHasBlockers = (
  preview?: SyntheticBondBatchPreview,
) => {
  if (!preview) return true;
  if ((preview.summary?.blocked_total || 0) > 0) return true;
  if ((preview.blocked_count || 0) > 0) return true;
  if ((preview.blockers || []).length > 0) return true;
  if (
    (preview.findings || []).some(
      finding => String(finding.severity).toUpperCase() === 'ERROR',
    )
  ) {
    return true;
  }
  return previewCandidates(preview).some(
    candidate =>
      (candidate.blockers || []).length > 0 ||
      (candidate.hard_blockers || []).length > 0,
  );
};

const BatchPreview: React.FC<Props> = ({
  draft,
  onSelectCandidate,
  preview,
  referenceCurves,
}) => {
  const candidates = previewCandidates(preview);
  const blockers = [
    ...(preview.blockers || []),
    ...(preview.findings || []).filter(
      finding => String(finding.severity).toUpperCase() === 'ERROR',
    ),
  ];
  const warnings = [
    ...(preview.warnings || []),
    ...(preview.findings || []).filter(
      finding => String(finding.severity).toUpperCase() === 'WARNING',
    ),
  ];
  const candidateWarnings = candidates.flatMap(candidate =>
    findingsFor(candidate).filter(
      finding => String(finding.severity).toUpperCase() !== 'ERROR',
    ),
  );
  const candidateCount =
    preview.summary?.candidate_total ||
    preview.asset_candidate_count ||
    preview.candidate_count ||
    candidates.length;
  const expectedRows =
    preview.summary?.projected_market_history_total ||
    preview.expected_market_history_rows ||
    preview.projected_market_history_rows ||
    candidates.reduce(
      (sum, candidate) =>
        sum +
        Number(
          candidate.expected_row_count || candidate.projected_row_count || 0,
        ),
      0,
    );
  const issueDates = preview.summary?.issue_dates || preview.issue_dates || [];
  const sourceNodes: PreviewSourceNode[] = preview.family?.source_nodes?.length
    ? preview.family.source_nodes
    : (referenceCurves?.tenors || [])
        .filter(slot => slot.asset)
        .map(slot => ({
          tenor_months: slot.tenor_months,
          tenor_label: slot.tenor_label,
          source_asset_id: slot.asset!.id,
          model: slot.asset!.model,
          name: slot.asset!.name,
          ticker: slot.asset!.ticker,
          ric: slot.asset!.ric,
        }));
  const grouped = useMemo(
    () =>
      [...draft.initial_tenors]
        .sort((a, b) => a - b)
        .map(tenor => ({
          tenor,
          candidates: candidates
            .filter(candidate => candidateTenor(candidate) === tenor)
            .sort((a, b) =>
              String(a.legal_issue_date || a.issue_date).localeCompare(
                String(b.legal_issue_date || b.issue_date),
              ),
            ),
        })),
    [candidates, draft.initial_tenors],
  );

  const columns: any[] = [
    {
      title: 'Legal dates',
      key: 'issue',
      width: 150,
      render: (_: unknown, candidate: SyntheticBondCandidate) => (
        <div className="synthetic-bonds__identity-cell">
          <Typography.Text>
            {dateOnly(candidate.legal_issue_date || candidate.issue_date)}
          </Typography.Text>
          <Typography.Text type="secondary">
            maturity{' '}
            {dateOnly(candidate.legal_maturity_date || candidate.maturity_date)}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Generated identity',
      key: 'identity',
      render: (_: unknown, candidate: SyntheticBondCandidate) => {
        const identifiers = candidateIdentifiers(candidate);
        return (
          <div className="synthetic-bonds__identity-cell">
            <Typography.Text strong>
              {identifiers.name || identifiers.ticker || 'Pending identity'}
            </Typography.Text>
            <Typography.Text type="secondary">
              {[identifiers.ticker, identifiers.ric, identifiers.isin]
                .filter(Boolean)
                .join(' · ') || '—'}
            </Typography.Text>
          </div>
        );
      },
    },
    {
      title: 'Actual coupon fixing',
      key: 'fixing',
      render: (_: unknown, candidate: SyntheticBondCandidate) => (
        <div className="synthetic-bonds__identity-cell">
          <Typography.Text>
            asset #{candidate.fixing?.source_asset_id || '—'} · row #
            {candidate.fixing?.source_market_history_id || '—'}
          </Typography.Text>
          <Typography.Text type="secondary">
            {timestamp(candidate) || 'No valid as-of fixing'}
          </Typography.Text>
          {candidate.fixing && (
            <Typography.Text type="secondary">
              bid {candidate.fixing.bid_yield ?? '—'}% · ask{' '}
              {candidate.fixing.ask_yield ?? '—'}% · age{' '}
              {candidate.fixing.age_days ?? '—'}d
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      title: 'Coupon',
      dataIndex: 'coupon',
      width: 90,
      render: (value: unknown) =>
        typeof value === 'number' ? `${value.toFixed(6)}%` : '—',
    },
    {
      title: 'Price horizon',
      key: 'horizon',
      width: 145,
      render: (_: unknown, candidate: SyntheticBondCandidate) => (
        <div className="synthetic-bonds__identity-cell">
          <Typography.Text>{dateOnly(candidate.priced_from)}</Typography.Text>
          <Typography.Text type="secondary">
            through {dateOnly(candidate.priced_through)}
          </Typography.Text>
          <Typography.Text type="secondary">
            first unpriced {dateOnly(candidate.first_unpriced_date)}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Interpolation sources',
      key: 'interpolation',
      width: 190,
      render: (_: unknown, candidate: SyntheticBondCandidate) => {
        const tenor = candidateTenor(candidate);
        const path = sourceNodes
          .filter(node => syntheticBondSourceNodeMonths(node) <= tenor * 12)
          .sort(
            (a, b) =>
              syntheticBondSourceNodeMonths(b) -
              syntheticBondSourceNodeMonths(a),
          )
          .map(
            node =>
              `${syntheticBondTenorLabel(
                syntheticBondSourceNodeMonths(node),
              )} #${node.source_asset_id}`,
          );
        return (
          <div className="synthetic-bonds__identity-cell">
            <Typography.Text>{path.join(' → ') || '—'}</Typography.Text>
            <Typography.Text type="secondary">
              Active adjacent pair changes with residual maturity; Graph shows
              each timestamp.
            </Typography.Text>
          </div>
        );
      },
    },
    {
      title: 'Rows',
      key: 'rows',
      width: 74,
      render: (_: unknown, candidate: SyntheticBondCandidate) =>
        candidate.expected_row_count || candidate.projected_row_count || 0,
    },
    {
      title: 'Review',
      key: 'review',
      width: 160,
      render: (_: unknown, candidate: SyntheticBondCandidate) => {
        const findings = findingsFor(candidate);
        return (
          <Space size={[4, 4]} wrap>
            {findings.map((finding, index) => (
              <Tooltip
                key={`${finding.code || 'finding'}-${index}`}
                title={findingText(finding)}
              >
                <Tag
                  color={
                    String(finding.severity).toUpperCase() === 'ERROR'
                      ? 'error'
                      : 'warning'
                  }
                >
                  {finding.code || finding.severity || 'finding'}
                </Tag>
              </Tooltip>
            ))}
            {!findings.length && <Tag color="success">Ready</Tag>}
          </Space>
        );
      },
    },
    {
      title: '',
      key: 'inspect',
      width: 90,
      render: (_: unknown, candidate: SyntheticBondCandidate) => {
        const id = syntheticBondCandidateId(candidate);
        return (
          <Typography.Link
            disabled={!id}
            onClick={() => id && onSelectCandidate(candidate)}
          >
            Graph
          </Typography.Link>
        );
      },
    },
  ];

  return (
    <Card
      className="synthetic-bonds__preview"
      title="2. Review the frozen plan"
    >
      <Typography.Paragraph type="secondary">
        This preview is the authority for Create. It has not created an AFSBond
        or written a MarketHistory row.
      </Typography.Paragraph>

      <div className="synthetic-bonds__stats">
        <Statistic
          title="Currency / family"
          value={draft.family_label || '—'}
        />
        <Statistic
          title="Tenors"
          value={draft.initial_tenors.join(', ')}
          suffix="Y"
        />
        <Statistic
          title="Issue dates"
          value={
            issueDates.length ||
            Math.max(...grouped.map(group => group.candidates.length), 0)
          }
        />
        <Statistic title="Assets in plan" value={candidateCount} />
        <Statistic
          title="Already identical"
          value={preview.summary?.existing_total || preview.existing_count || 0}
        />
        <Statistic title="Expected price rows" value={expectedRows} />
        <Statistic
          title="Blocked"
          value={
            preview.summary?.blocked_total ||
            preview.blocked_count ||
            blockers.length
          }
        />
        <Statistic
          title="Warnings"
          value={warnings.length + candidateWarnings.length}
        />
      </div>

      <Descriptions
        bordered
        className="synthetic-bonds__frozen-details"
        column={{ xs: 1, md: 2 }}
        size="small"
      >
        <Descriptions.Item label="Generation as-of">
          {String(preview.generation_as_of_date || '—')}
        </Descriptions.Item>
        <Descriptions.Item label="Family revision / hash">
          {String(
            preview.family?.id ||
              preview.family?.definition_hash ||
              preview.family_revision_id ||
              preview.family_definition_hash ||
              preview.family_hash ||
              '—',
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Legal issue range">
          {draft.first_issue_date} → {draft.last_issue_date}
        </Descriptions.Item>
        <Descriptions.Item label="Target">
          {draft.target_kind}
        </Descriptions.Item>
        <Descriptions.Item label="Reviewed sources" span={2}>
          <Space size={[4, 4]} wrap>
            {(preview.family?.source_nodes || []).length
              ? preview.family.source_nodes.map(node => (
                  <Tag key={syntheticBondSourceNodeMonths(node)}>
                    {syntheticBondTenorLabel(
                      syntheticBondSourceNodeMonths(node),
                    )}{' '}
                    · {node.name} · #{node.source_asset_id}
                  </Tag>
                ))
              : (referenceCurves?.tenors || []).map(slot => (
                  <Tag key={slot.tenor_months}>
                    {slot.tenor_label} ·{' '}
                    {slot.asset?.name ||
                      `#${
                        draft.source_asset_ids_by_tenor_months[
                          String(slot.tenor_months)
                        ]
                      }`}
                  </Tag>
                ))}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Uniform family metadata" span={2}>
          <Space size={[4, 4]} wrap>
            {SYNTHETIC_BOND_FAMILY_METADATA_FIELDS.map(
              field =>
                [
                  field,
                  (preview.family?.family_metadata || draft.family_metadata)[
                    field
                  ],
                ] as const,
            )
              .filter(([, value]) => value !== null && String(value) !== '')
              .map(([field, value]) => (
                <Tag key={field}>
                  {field}: {String(value)}
                </Tag>
              ))}
          </Space>
        </Descriptions.Item>
      </Descriptions>

      {(blockers.length > 0 || warnings.length > 0) && (
        <div className="synthetic-bonds__batch-findings">
          {blockers.map((finding, index) => (
            <Tag color="error" key={`blocker-${finding.code || index}`}>
              {finding.code || 'Blocked'}: {findingText(finding)}
            </Tag>
          ))}
          {warnings.map((finding, index) => (
            <Tag color="warning" key={`warning-${finding.code || index}`}>
              {finding.code || 'Warning'}: {findingText(finding)}
            </Tag>
          ))}
        </div>
      )}

      {candidates.length ? (
        <Collapse
          className="synthetic-bonds__tenor-review"
          items={grouped.map(group => ({
            key: String(group.tenor),
            label: (
              <span>
                <strong>{group.tenor}Y vintages</strong> ·{' '}
                {group.candidates.length} candidates
                {group.tenor === 2 && (
                  <Tag className="synthetic-bonds__inline-tag">
                    issue snapshot only
                  </Tag>
                )}
              </span>
            ),
            children: (
              <Table
                columns={columns}
                dataSource={group.candidates}
                pagination={{ pageSize: 8, hideOnSinglePage: true }}
                rowKey={candidate =>
                  syntheticBondCandidateId(candidate) ||
                  `${group.tenor}-${candidate.issue_date}`
                }
                scroll={{ x: 1310 }}
                size="small"
              />
            ),
          }))}
        />
      ) : (
        <Empty description="The server returned no candidate detail rows." />
      )}
    </Card>
  );
};

export default React.memo(BatchPreview);
